import { Link, useHydrated } from "@tanstack/react-router"
import { useId } from "react"

import { TurnstileWidget } from "@repo/captcha/components/turnstile-widget"
import { useServerFunctionForm } from "@repo/server-function-form/index"
import { Button } from "@repo/ui/components/ui/button"
import { Field, FieldLabel, FieldMessage } from "@repo/ui/components/ui/field"
import { TextField } from "@repo/ui/components/ui/form-fields"
import { Input } from "@repo/ui/components/ui/input"
import { Separator } from "@repo/ui/components/ui/separator"
import { toast } from "@repo/ui/components/ui/sonner"

import { signInWithPasswordFn } from "../functions"
import { signInWithPasswordSchema } from "../schemas"

export type SignInFormProps = {
  showSignUp?: boolean
  onSignedIn?: () => void | Promise<void>
  /** Called when the password was correct but a second factor is required. */
  onTwoFactorRequired?: (methods: string[]) => void | Promise<void>
}

export function SignInForm({
  showSignUp,
  onSignedIn,
  onTwoFactorRequired,
}: SignInFormProps) {
  const toastId = useId()
  const isHydrated = useHydrated()

  const { form, formId, formMutation, handleFormSubmit } =
    useServerFunctionForm(signInWithPasswordFn, signInWithPasswordSchema, {
      formId: "sign-in-form",
      defaultValues: {
        email: "",
        password: "",
      },
      useCaptcha: true,
      onSubmit() {
        toast.loading("Signing in...", { id: toastId })
      },
      onSuccess: async (result) => {
        if (result?.twoFactorRequired) {
          toast.dismiss(toastId)
          await onTwoFactorRequired?.(result.twoFactorMethods ?? [])
          return
        }
        toast.success("Signed in", { id: toastId })
        await onSignedIn?.()
      },
      onError() {
        toast.error("Failed to sign in", { id: toastId })
      },
    })

  return (
    <form
      id={formId}
      noValidate
      data-e2e-ready={isHydrated ? "true" : "false"}
      onSubmit={handleFormSubmit}
    >
      <div className="flex flex-col gap-4">
        <form.Field name="email">
          {(field) => (
            <TextField
              field={field}
              label="Email"
              type="email"
              placeholder="joe@example.com"
              disabled={!isHydrated}
              inputProps={{ autoComplete: "email" }}
            />
          )}
        </form.Field>
        <form.Field name="password">
          {(field) => (
            <Field data-invalid={field.state.meta.errors.length > 0}>
              <div className="flex w-full items-center justify-between">
                <FieldLabel htmlFor={field.name}>Password</FieldLabel>
                <Link
                  to="/auth/forgot-password"
                  className="ml-auto inline-block text-sm underline-offset-4 hover:underline"
                  viewTransition
                  tabIndex={-1}
                >
                  Forgot your password?
                </Link>
              </div>
              <Input
                id={field.name}
                name={field.name}
                type="password"
                placeholder=""
                autoComplete="current-password"
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value)}
                onBlur={field.handleBlur}
                disabled={!isHydrated}
                aria-invalid={field.state.meta.errors.length > 0}
              />
              <FieldMessage errors={field.state.meta.errors} />
            </Field>
          )}
        </form.Field>
        {formMutation.error?.message && (
          <p className="bg-destructive/50 text-destructive-foreground rounded-md px-2 py-1 text-sm">
            {formMutation.error.message}
          </p>
        )}
        <TurnstileWidget />
        <div className="grid gap-4">
          <Button
            form={formId}
            type="submit"
            className="w-full"
            disabled={!isHydrated}
            nativeButton
            isLoading={formMutation.isPending}
            loadingText="Signing in..."
          >
            Sign In
          </Button>
          {showSignUp ? (
            <>
              <div className="relative h-5">
                <Separator className="absolute inset-0 top-1/2" />
                <span className="bg-card text-muted-foreground relative mx-auto block w-fit px-2 text-xs">
                  or
                </span>
              </div>
              <Button
                variant="outline"
                className="w-full"
                nativeButton={false}
                render={<Link to="/auth/sign-up" viewTransition />}
              >
                Create an Account
              </Button>
            </>
          ) : null}
        </div>
      </div>
    </form>
  )
}
